import { MyReviewResponse } from '@tmap-web-lib/remote-api-client/frontman'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { sendEventLog } from '@/features/log/log.fn'
import MyLogDefinition from '@/features/log/log.my'
import { useOpenService } from '@/hooks'

import * as styles from './placeReview.css'

interface Props {
  data: MyReviewResponse | null
}

function PlaceReview({ data }: Props) {
  const openService = useOpenService()
  const { action_id } = MyLogDefinition.my
  const reviewData = data?.data || null
  const [_isNarrow, setIsNarrow] = useState(false)

  const callMoveToReviewHomeDebouncing = useMemo(
    () =>
      debounce(
        (actionEvent: string) => {
          sendEventLog(actionEvent)
          openService('tmap://place?productid=review&pageid=home&portraitonly=true')
        },
        1000,
        { leading: true, trailing: false }
      ),
    [openService]
  )

  const moveToReviewHome = useCallback(
    (actionEvent: string) => {
      callMoveToReviewHomeDebouncing(actionEvent)
    },
    [callMoveToReviewHomeDebouncing]
  )

  const callMoveToReview = useMemo(
    () =>
      debounce(
        () => {
          if (reviewData?.writeModule?.reviewId) {
            sendEventLog(action_id?.tapWritingReview)
            openService(
              `tmap://place?productid=review&pageid=write&portraitonly=true&extra=${encodeURIComponent(
                JSON.stringify({
                  poiId: reviewData?.writeModule?.poiId,
                  reviewId: reviewData?.writeModule?.reviewId,
                })
              )}`
            )
          } else {
            sendEventLog(action_id?.tapAvailableReview)
            openService(
              `tmap://place?productid=review&pageid=write&portraitonly=true&extra=${encodeURIComponent(JSON.stringify({ poiId: reviewData?.writeModule?.poiId }))}`
            )
          }
        },
        1000,
        {
          leading: true,
          trailing: false,
        }
      ),
    [openService, reviewData, action_id?.tapAvailableReview, action_id?.tapWritingReview]
  )

  const moveToReview = useCallback(() => {
    callMoveToReview()
  }, [callMoveToReview])

  const moreReviewBtn = useMemo(() => {
    if (reviewData) {
      return (
        <button
          data-testid="review_write_btn"
          className={styles.moreReviewBtn}
          onClick={() =>
            action_id?.tapAvailableReviewMore && moveToReviewHome(action_id.tapAvailableReviewMore)
          }
        >
          <>
            작성 가능한&nbsp;<span>{reviewData?.moreModule?.visitCnt}개</span>&nbsp;리뷰 쓰기
          </>
        </button>
      )
    }
  }, [reviewData, moveToReviewHome, action_id?.tapAvailableReviewMore])

  const displayReviewModule = useMemo(() => {
    if (reviewData) {
      return (
        <>
          <div
            data-testid="review_module"
            className={styles.reviewModuleWrapper}
            onClick={moveToReview}
          >
            <p>{reviewData?.writeModule?.poiName}</p>
            <div className={styles.tripDateTag}>{reviewData?.writeModule?.visitLabel}</div>
            <button className={styles.reviewBtn}>{reviewData?.writeModule?.buttonText}</button>
          </div>
          {reviewData?.moreModule ? moreReviewBtn : null}
        </>
      )
    }
  }, [reviewData, moveToReview, moreReviewBtn])

  const displayReview = useMemo(() => {
    if (reviewData) {
      return <>{reviewData.visible && displayReviewModule}</>
    }
  }, [reviewData, displayReviewModule])

  useEffect(() => {
    const handleResize = () => {
      setIsNarrow(window.innerWidth <= 320)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!reviewData?.writeModule?.reviewId) {
      sendEventLog(action_id?.viewAvailableReview)
    } else {
      sendEventLog(action_id?.viewWritingReview)
    }
  }, [reviewData, action_id?.viewAvailableReview, action_id?.viewWritingReview])

  useEffect(() => {
    if (reviewData?.moreModule?.text) {
      sendEventLog(action_id?.viewAvailableReviewMore, undefined, undefined, 'mixpanel')
    }
  }, [reviewData, action_id?.viewAvailableReviewMore])

  return <div className={styles.wrapper}>{data?.code !== 200 ? null : displayReview}</div>
}

export { PlaceReview }
